import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import type { NeedsAttentionItem } from '@gokrew/models/company-dashboard';
import { Button } from '@/components/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover';
import { cn } from '@/lib/utils';
import { needsAttentionDisplayParams } from '@/utility/needsAttention';
import styles from './NeedsAttention.module.css';

/**
 * "Why?" under a line's sentence (spec 3.2, D152): a popover listing the
 * line's reasons, one per fired factor on a signal (plus its footer), the
 * family line on any other line. Copy in the portal namespace.
 */
export const WhyLink = ({
  item,
  namespace,
  onPrepare,
}: {
  item: NeedsAttentionItem;
  namespace: string;
  onPrepare?: (item: NeedsAttentionItem) => void;
}) => {
  const { t, i18n } = useTranslation(namespace);
  const { t: tl } = useTranslation(TranslationNS.LibsCommon);
  if (!item.why?.length) return null;
  const canPrepare = !!item.prepare && !!onPrepare;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={styles.whyLink}>
          {t('needs_attention.why_link')}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={styles.whyPopover}>
        <ul className={styles.whyList}>
          {item.why.map((reason, index) => (
            <li
              key={`${reason.key}-${index}`}
              className={cn(
                styles.whyLine,
                reason.key.endsWith('.footer') && styles.whyFooter
              )}
            >
              {t(reason.key, {
                ...needsAttentionDisplayParams(
                  reason.params ?? {},
                  t,
                  i18n.language
                ),
                defaultValue: '',
              })}
            </li>
          ))}
        </ul>
        {canPrepare && (
          // "Prepare the meeting" (spec 7.1): drafted from these reasons only.
          <div className={styles.whyPrepare}>
            <Button
              variant="outline"
              size="xs"
              IconPrefix={Sparkles}
              onClick={() => onPrepare(item)}
            >
              {t('needs_attention.actions.prepare_meeting')}
            </Button>
            <span className={styles.whyPrepareHint}>
              {tl('needs_attention_prepare.button_hint')}
            </span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
